import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Search } from "lucide-react";
import { useLocalePath } from "@/components/LocaleRouter";
import { useTikTokSearch, type TikTokUser } from "@/hooks/useTikTokSearch";
import { useIgSearch, type IgUser } from "@/hooks/useInstagramSearch";
import { useYtSearch, parseYouTubeUrl, type YtChannel, type YtVideo } from "@/hooks/useYouTubeSearch";
import { useTwitchSearch, type TwitchChannel } from "@/hooks/useTwitchSearch";
import { useDeezerArtistSearch, type DeezerArtist } from "@/hooks/useDeezerArtistSearch";
import { useOrder } from "@/context/OrderContext";
import { InstagramProfileImage } from "@/components/InstagramProfileImage";

export type PlatformId =
  | "tiktok"
  | "youtube"
  | "spotify"
  | "instagram"
  | "twitch"
  | "twitter"
  | "facebook";

interface SearchPanelProps {
  platform: PlatformId;
  ctaLabel: string;
}

const formatCount = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const PLACEHOLDERS: Record<PlatformId, string> = {
  tiktok: "Enter your TikTok username (e.g. @charlidamelio)",
  youtube: "Search a YouTube channel/video or paste a URL",
  spotify: "Search a Spotify artist (e.g. Drake, Taylor Swift)",
  instagram: "Enter your Instagram username (e.g. @cristiano)",
  twitch: "Search a Twitch channel (e.g. ninja, pokimane)",
  twitter: "Paste an X/Twitter profile or tweet URL",
  facebook: "Paste a Facebook page, post or video URL",
};

/* ==================== TikTok ==================== */
const TikTokSearch = ({ ctaLabel }: { ctaLabel: string }) => {
  const [username, setUsername] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const { users, loading, searched } = useTikTokSearch(username);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const localePath = useLocalePath();
  const { setUser } = useOrder();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = (u: TikTokUser) => {
    setUser?.(u as any);
    setShowDropdown(false);
    navigate(localePath(`/create-order/${u.unique_id}`));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = username.replace(/^@/, "").trim();
    if (trimmed.length >= 2) navigate(localePath(`/create-order/${trimmed}`));
  };

  const trimmed = username.replace(/^@/, "").trim();
  const showPanel = showDropdown && trimmed.length >= 2;

  return (
    <SearchShell
      wrapperRef={wrapperRef}
      placeholder={PLACEHOLDERS.tiktok}
      value={username}
      onChange={(v) => { setUsername(v); setShowDropdown(true); }}
      onFocus={() => setShowDropdown(true)}
      loading={loading}
      onSubmit={handleSubmit}
      ctaLabel={ctaLabel}
    >
      {showPanel && (
        <Dropdown loading={loading} empty={searched && users.length === 0}>
          {users.map((u) => (
            <ResultRow
              key={u.uid || u.unique_id}
              avatar={u.avatar}
              title={u.unique_id}
              subtitle={`${u.nickname} · ${formatCount(u.follower_count)} followers`}
              onClick={() => handleSelect(u)}
            />
          ))}
        </Dropdown>
      )}
    </SearchShell>
  );
};

/* ==================== Instagram ==================== */
const InstagramSearch = ({ ctaLabel }: { ctaLabel: string }) => {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const trimmed = query.replace(/^@/, "").trim();
  const { users, loading, searched } = useIgSearch(trimmed);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const localePath = useLocalePath();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = (u: IgUser) => {
    setShowDropdown(false);
    navigate(localePath(`/instagram/create-order/${u.username}`));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (trimmed.length >= 2) navigate(localePath(`/instagram/create-order/${trimmed}`));
  };

  const showPanel = showDropdown && trimmed.length >= 2;

  return (
    <SearchShell
      wrapperRef={wrapperRef}
      placeholder={PLACEHOLDERS.instagram}
      value={query}
      onChange={(v) => { setQuery(v); setShowDropdown(true); }}
      onFocus={() => setShowDropdown(true)}
      loading={loading}
      onSubmit={handleSubmit}
      ctaLabel={ctaLabel}
    >
      {showPanel && (
        <Dropdown loading={loading} empty={searched && users.length === 0}>
          {users.map((u) => (
            <button
              key={u.username}
              type="button"
              onClick={() => handleSelect(u)}
              className="flex w-full items-center gap-4 rounded-xl px-4 py-3.5 text-left transition-colors hover:bg-secondary"
            >
              <InstagramProfileImage
                src={u.profile_pic}
                alt={u.username}
                className="h-12 w-12 rounded-full object-cover bg-muted shrink-0 border border-border"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">@{u.username}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {u.full_name} · {formatCount(u.followers || 0)} followers
                </p>
              </div>
            </button>
          ))}
        </Dropdown>
      )}
    </SearchShell>
  );
};

/* ==================== YouTube ==================== */
const YouTubeSearch = ({ ctaLabel }: { ctaLabel: string }) => {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const { channels, videos, loading, searched, stale } = useYtSearch(query);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const localePath = useLocalePath();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goChannel = (c: YtChannel) => {
    setShowDropdown(false);
    navigate(localePath(`/youtube/select-videos/${c.id}`));
  };
  const goVideo = (v: YtVideo) => {
    setShowDropdown(false);
    navigate(localePath(`/youtube/create-order/video/${v.id}`));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseYouTubeUrl(query);
    if (parsed?.kind === "video") navigate(localePath(`/youtube/create-order/video/${parsed.id}`));
    else if (parsed?.kind === "channel") navigate(localePath(`/youtube/select-videos/${parsed.id}`));
    else navigate(localePath("/youtube"));
  };

  const showPanel = showDropdown && query.trim().length >= 2;
  const hasResults = channels.length > 0 || videos.length > 0;

  return (
    <SearchShell
      wrapperRef={wrapperRef}
      placeholder={PLACEHOLDERS.youtube}
      value={query}
      onChange={(v) => { setQuery(v); setShowDropdown(true); }}
      onFocus={() => setShowDropdown(true)}
      loading={loading}
      onSubmit={handleSubmit}
      ctaLabel={ctaLabel}
    >
      {showPanel && (
        <Dropdown loading={loading && !hasResults} empty={searched && !hasResults} stale={stale}>
          {channels.length > 0 && (
            <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Channels
            </div>
          )}
          {channels.slice(0, 4).map((c) => (
            <ResultRow
              key={c.id}
              avatar={c.thumb?.startsWith("//") ? `https:${c.thumb}` : c.thumb}
              title={c.name}
              handle={c.handle || undefined}
              metric={c.subscribers > 0 ? `${formatCount(c.subscribers)} subs` : undefined}
              subtitle={c.description ? c.description.slice(0, 80) : undefined}
              onClick={() => goChannel(c)}
            />
          ))}
          {videos.length > 0 && (
            <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Videos
            </div>
          )}
          {videos.slice(0, 4).map((v) => (
            <ResultRow
              key={v.id}
              avatar={v.thumb}
              avatarSquare
              title={v.title}
              subtitle={`${formatCount(v.views || 0)} views${v.author ? ` · ${v.author}` : ""}`}
              onClick={() => goVideo(v)}
            />
          ))}
        </Dropdown>
      )}
    </SearchShell>
  );
};

/* ==================== Spotify (via Deezer artist search) ==================== */
const SpotifySearch = ({ ctaLabel }: { ctaLabel: string }) => {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const { items, loading } = useDeezerArtistSearch(query);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const localePath = useLocalePath();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = (a: DeezerArtist) => {
    setShowDropdown(false);
    const img = a.picture_xl ?? a.picture_big ?? a.picture_medium;
    const params = new URLSearchParams();
    params.set("artist", a.name);
    params.set("dzId", String(a.id));
    if (img) params.set("img", img);
    navigate(`${localePath("/spotify/create")}?${params.toString()}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(localePath("/spotify"));
  };

  const showPanel = showDropdown && query.trim().length >= 2;

  return (
    <SearchShell
      wrapperRef={wrapperRef}
      placeholder={PLACEHOLDERS.spotify}
      value={query}
      onChange={(v) => { setQuery(v); setShowDropdown(true); }}
      onFocus={() => setShowDropdown(true)}
      loading={loading}
      onSubmit={handleSubmit}
      ctaLabel={ctaLabel}
    >
      {showPanel && (
        <Dropdown loading={loading} empty={!loading && items.length === 0}>
          {items.map((a) => (
            <ResultRow
              key={a.id}
              avatar={a.picture_medium || a.picture_big}
              title={a.name}
              subtitle={a.nb_fan ? `${formatCount(a.nb_fan)} fans` : "Spotify artist"}
              onClick={() => handleSelect(a)}
            />
          ))}
        </Dropdown>
      )}
    </SearchShell>
  );
};

/* ==================== Twitch ==================== */
const TwitchSearchPanel = ({ ctaLabel }: { ctaLabel: string }) => {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const { channels, loading, searched, stale } = useTwitchSearch(query);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const localePath = useLocalePath();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = (c: TwitchChannel) => {
    setShowDropdown(false);
    navigate(`${localePath("/twitch")}?u=${encodeURIComponent(c.login)}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q) navigate(`${localePath("/twitch")}?u=${encodeURIComponent(q)}`);
    else navigate(localePath("/twitch"));
  };

  const showPanel = showDropdown && query.trim().length >= 2;

  return (
    <SearchShell
      wrapperRef={wrapperRef}
      placeholder={PLACEHOLDERS.twitch}
      value={query}
      onChange={(v) => { setQuery(v); setShowDropdown(true); }}
      onFocus={() => setShowDropdown(true)}
      loading={loading}
      onSubmit={handleSubmit}
      ctaLabel={ctaLabel}
    >
      {showPanel && (
        <Dropdown loading={loading} empty={searched && channels.length === 0} stale={stale}>
          {channels.map((c) => (
            <ResultRow
              key={c.id}
              avatar={c.thumb}
              title={c.name || c.login}
              subtitle={c.isLive ? `🔴 LIVE${c.gameName ? ` · ${c.gameName}` : ""}` : c.gameName || `@${c.login}`}
              onClick={() => handleSelect(c)}
            />
          ))}
        </Dropdown>
      )}
    </SearchShell>
  );
};

/* ==================== Twitter / Facebook (URL submit, no live results) ==================== */
const SimpleUrlSearch = ({
  platform,
  ctaLabel,
  destination,
}: {
  platform: PlatformId;
  ctaLabel: string;
  destination: string;
}) => {
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  const localePath = useLocalePath();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (v) navigate(`${localePath(destination)}?u=${encodeURIComponent(v)}`);
    else navigate(localePath(destination));
  };

  return (
    <SearchShell
      placeholder={PLACEHOLDERS[platform]}
      value={value}
      onChange={setValue}
      loading={false}
      onSubmit={handleSubmit}
      ctaLabel={ctaLabel}
    />
  );
};

/* ==================== Shared shell ==================== */
interface SearchShellProps {
  wrapperRef?: React.RefObject<HTMLDivElement>;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onFocus?: () => void;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  ctaLabel: string;
  children?: React.ReactNode;
}

const SearchShell = ({
  wrapperRef,
  placeholder,
  value,
  onChange,
  onFocus,
  loading,
  onSubmit,
  ctaLabel,
  children,
}: SearchShellProps) => (
  <div ref={wrapperRef} className="relative mx-auto mt-6 sm:mt-8 max-w-2xl">
    <form
      onSubmit={onSubmit}
      className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0 sm:rounded-2xl sm:border sm:border-border sm:bg-card sm:overflow-hidden"
      style={{ boxShadow: "var(--glow-cyan)" }}
    >
      <div className="relative flex-1 rounded-2xl border border-border bg-card shadow-sm sm:rounded-none sm:border-0 sm:shadow-none overflow-hidden">
        <div className="pointer-events-none absolute inset-y-0 left-4 sm:left-5 flex items-center">
          <Search className="h-5 w-5 text-primary" />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          placeholder={placeholder}
          className="w-full border-0 bg-transparent pl-12 sm:pl-14 pr-4 py-4 sm:py-5 text-sm sm:text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        {loading && (
          <div className="absolute inset-y-0 right-4 flex items-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
      </div>
      <button
        type="submit"
        className="inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-none px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base font-bold text-primary-foreground transition-all hover:opacity-90 active:scale-[0.98]"
        style={{ background: "var(--hero-gradient)", boxShadow: "var(--glow-button)" }}
      >
        {ctaLabel}
        <ArrowRight className="h-5 w-5" />
      </button>
    </form>
    {children}
  </div>
);

const Dropdown = ({
  loading,
  empty,
  stale,
  children,
}: {
  loading: boolean;
  empty: boolean;
  stale?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className="absolute left-0 right-0 top-full z-[200] mt-3 max-h-[420px] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl"
    style={{ boxShadow: "0 8px 32px hsl(0 0% 0% / 0.6), 0 0 20px hsl(185 100% 50% / 0.1)" }}
  >
    {stale && !loading && (
      <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-4 py-2">
        <span
          className="inline-flex h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: "hsl(45 100% 60%)" }}
        />
        <p className="text-[11px] font-medium text-muted-foreground">
          Cached result — live data temporarily unavailable
        </p>
      </div>
    )}
    {loading ? (
      <div className="space-y-1 p-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-12 w-12 animate-pulse rounded-full bg-muted shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 animate-pulse rounded bg-muted" />
              <div className="h-3 w-44 animate-pulse rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    ) : empty ? (
      <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
        <Search className="h-7 w-7 text-muted-foreground/50" />
        <p className="text-sm font-medium text-muted-foreground">No results found</p>
      </div>
    ) : (
      <div className="p-2">{children}</div>
    )}
  </div>
);

const ResultRow = ({
  avatar,
  avatarSquare,
  title,
  subtitle,
  handle,
  metric,
  onClick,
}: {
  avatar?: string;
  avatarSquare?: boolean;
  title: string;
  subtitle?: string;
  /** Prominent handle (e.g. @pjm5149) — shown in primary color, monospace */
  handle?: string;
  /** Prominent metric (e.g. "22.1K subs") — bold to disambiguate same-named results */
  metric?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-xl px-4 py-3.5 text-left transition-colors hover:bg-secondary"
  >
    {avatar ? (
      <img
        src={avatar}
        alt=""
        referrerPolicy="no-referrer"
        className={`h-12 ${avatarSquare ? "w-20 rounded-md" : "w-12 rounded-full"} object-cover bg-muted shrink-0 border border-border`}
      />
    ) : (
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground text-base font-bold shrink-0">
        {title.charAt(0)?.toUpperCase() || "?"}
      </div>
    )}
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-foreground">{title}</p>
      {(handle || metric) && (
        <div className="mt-0.5 flex items-center gap-1.5 text-xs">
          {handle && (
            <span className="truncate font-mono font-semibold text-primary">{handle}</span>
          )}
          {handle && metric && <span className="text-muted-foreground/50">·</span>}
          {metric && <span className="font-bold text-foreground">{metric}</span>}
        </div>
      )}
      {subtitle && (
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</p>
      )}
    </div>
  </button>
);

/* ==================== Main exported wrapper ==================== */
export const UniversalPlatformSearch = ({
  platform,
  ctaLabel = "Continue",
}: {
  platform: PlatformId;
  ctaLabel?: string;
}) => {
  switch (platform) {
    case "tiktok":
      return <TikTokSearch ctaLabel={ctaLabel} />;
    case "instagram":
      return <InstagramSearch ctaLabel={ctaLabel} />;
    case "youtube":
      return <YouTubeSearch ctaLabel={ctaLabel} />;
    case "spotify":
      return <SpotifySearch ctaLabel={ctaLabel} />;
    case "twitch":
      return <TwitchSearchPanel ctaLabel={ctaLabel} />;
    case "twitter":
      return <SimpleUrlSearch platform="twitter" ctaLabel={ctaLabel} destination="/twitter" />;
    case "facebook":
      return <SimpleUrlSearch platform="facebook" ctaLabel={ctaLabel} destination="/facebook" />;
  }
};
